/**
 * Role-Based Layout — DeCarbonIQ Platform
 *
 * Renders dynamic sidebar navigation per active workspace role.
 * All 6 roles: SME | Project | Corporate | Financial Institution | States | Training
 *
 * RBAC upgrade path:
 *   1. Add user.roles[] from backend
 *   2. Filter ROLES_CONFIG entries via canAccessRole(roleId)
 *   3. Add requiredPermission checks per nav item
 *   4. Server-side: validate role claim on sensitive endpoints
 */
import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth, ROLE_META, ALL_ROLES } from "@/context/AuthContext";
import { useJourney } from "@/context/JourneyContext";
import WelcomeToast from "@/components/WelcomeToast";
import {
  LayoutDashboard, Building2, ClipboardList, FolderOpen, FileText, Globe,
  BookOpen, BookMarked, Settings, Shield, ChevronDown, ChevronRight, LogOut,
  Bell, Award, Menu, X, BarChart3, Sun, FileCheck, TrendingUp, Leaf, Zap, Package,
  Layers, Database, BarChart2, Activity, Target, TrendingDown, GraduationCap,
  Check, User, Flag, Compass, DollarSign, MapPin, Lock, Clock, GitBranch, Map,
  PlusCircle, CheckCircle2, Recycle
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { FI_MODULES } from "@/pages/app/fi/fiModules";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

// ── Dangote Corporate Sidebar — matches GenericProjectSidebar styling exactly ──
function DangoteSidebar({ onClose, location }) {
  const buMatch = location.pathname.match(/\/corporate\/bu\/([^/]+)/);
  const assetMatch = location.pathname.match(/\/corporate\/asset\/([^/]+)/);

  const items = [
    { label: "Dashboard",             path: "/corporate/dashboard",         icon: BarChart2  },
    { label: "Monthly Reporting",     path: "/corporate/reporting",          icon: ClipboardList },
    { label: "Climate & Circularity", path: "/corporate/climate",            icon: Leaf       },
    { label: "SDG Contribution",      path: "/corporate/sdg",                icon: Activity   },
    { label: "Carbon Value Bridge",   path: "/corporate/value-bridge",       icon: GitBranch  },
    { label: "Carbon Strategy",       path: "/corporate/carbon-strategy",    icon: Layers     },
    { label: "Carbon Structuring",    path: "/corporate/carbon-structuring", icon: FolderOpen },
    { label: "Evidence Vault",        path: "/corporate/evidence-vault",     icon: FileText   },
    { label: "Financing",             path: "/corporate/financing",          icon: DollarSign },
    { label: "Reports",               path: "/corporate/reports",            icon: BarChart2  },
  ];

  return (
    <div className="space-y-0.5">
      <p className="text-[9px] text-slate-500 uppercase tracking-widest px-3 pb-1 pt-2 font-semibold">
        Dangote Industries
      </p>
      {items.map(({ label, path, icon: Icon }) => {
        const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
        return (
          <NavLink key={path} to={path} onClick={onClose}
            className={() =>
              `flex items-center gap-2.5 px-3 py-2 rounded-r-lg text-[12.5px] font-medium transition-all border-l-2 ${
                isActive
                  ? "border-[#059669] bg-emerald-600/12 text-emerald-300"
                  : "border-transparent text-slate-400 hover:bg-slate-800/50 hover:text-white hover:border-slate-600"
              }`
            }>
            <Icon size={14} strokeWidth={isActive ? 2 : 1.75} className="flex-shrink-0" />
            <span className="flex-1 truncate">{label}</span>
          </NavLink>
        );
      })}
    </div>
  );
}

// ── Nigeria Breweries Plc Sidebar ─────────────────────────────────────────────
function NBPLCSidebar({ onClose, location }) {
  const items = [
    { label: "Dashboard",             path: "/corporate/nbplc/dashboard",         icon: BarChart2   },
    { label: "Monthly Reporting",     path: "/corporate/nbplc/reporting",          icon: ClipboardList},
    { label: "Climate & Circularity", path: "/corporate/nbplc/climate",            icon: Leaf        },
    { label: "SDG Contribution",      path: "/corporate/nbplc/sdg",                icon: Activity    },
    { label: "Carbon Value Bridge",   path: "/corporate/nbplc/value-bridge",       icon: GitBranch   },
    { label: "Carbon Strategy",       path: "/corporate/nbplc/carbon-strategy",    icon: Layers      },
    { label: "Carbon Structuring",    path: "/corporate/nbplc/carbon-structuring", icon: FolderOpen  },
    { label: "Evidence Vault",        path: "/corporate/nbplc/evidence-vault",     icon: FileText    },
    { label: "Financials",             path: "/corporate/nbplc/financing",         icon: DollarSign  },
    { label: "Reports",               path: "/corporate/nbplc/reports",            icon: BarChart2   },
  ];
  return (
    <div className="space-y-0.5">
      <p className="text-[9px] text-slate-500 uppercase tracking-widest px-3 pb-1 pt-2 font-semibold">
        Nigeria Breweries Plc
      </p>
      {items.map(({ label, path, icon: Icon }) => {
        const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
        return (
          <NavLink key={path} to={path} onClick={onClose}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              isActive ? "bg-amber-900/20 text-amber-300" : "text-slate-400 hover:bg-slate-800 hover:text-white"
            }`}>
            <Icon size={13} className={isActive ? "text-amber-400" : "text-slate-500"} />
            <span className="flex-1 truncate">{label}</span>
          </NavLink>
        );
      })}
    </div>
  );
}

// ── Nosak Group Sidebar — Consolidated 7-Page Structure ──────────────────────
function NosAKSidebar({ onClose, location }) {
  const [badges, setBadgesState] = React.useState(() => {
    try { const { getBadges } = require("../context/NosAKBadgeStore"); return getBadges(); } catch { return {}; }
  });

  React.useEffect(() => {
    try {
      const { subscribeBadges } = require("../context/NosAKBadgeStore");
      return subscribeBadges(b => setBadgesState({...b}));
    } catch { return () => {}; }
  }, []);

  const BADGE_MAP = {
    "/nosak/finance":   badges.financeReady > 0 ? badges.financeReady : null,
    "/nosak/evidence":  badges.evidenceGaps > 0 ? badges.evidenceGaps : null,
    "/nosak/esg":       badges.highRisks > 0 ? badges.highRisks : null,
    "/nosak/dashboard": badges.boardAlerts > 0 ? "!" : null,
  };

  const items = [
    { section: "EXECUTIVE",          label: "Executive Dashboard",        path: "/nosak/dashboard",    icon: BarChart2,  accent: "#f59e0b" },
    { section: "OPERATIONAL ENGINE", label: "Data Workspace (SSOT)",      path: "/nosak/workspace",    icon: Database,   accent: "#6366f1" },
    { section: "ANALYTICS",          label: "Climate & Circularity",      path: "/nosak/intelligence", icon: Leaf,       accent: "#059669" },
    { section: "FINANCE & STRATEGY", label: "Finance & Carbon Markets",   path: "/nosak/finance",      icon: DollarSign, accent: "#3b82f6" },
    { section: "GOVERNANCE",         label: "ESG, Risk & Disclosure",     path: "/nosak/esg",          icon: Shield,     accent: "#8b5cf6" },
    { section: "MRV & VERIFICATION", label: "Evidence Vault & Registry",  path: "/nosak/evidence",     icon: FileText,   accent: "#0891b2" },
    { section: "ADMIN",              label: "Settings & Administration",  path: "/nosak/settings",     icon: Settings,   accent: "#64748b" },
  ];

  return (
    <div className="space-y-1">
      <div className="px-3 py-2">
        <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-0.5">Nosak Group</p>
        <p className="text-[8px] text-slate-600">Enterprise Climate Intelligence</p>
      </div>
      {items.map(({ label, path, icon: Icon, accent }) => {
        const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
        const badge = BADGE_MAP[path];
        return (
          <NavLink key={path} to={path} onClick={onClose}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
              isActive ? "bg-slate-700/60 text-white" : "text-slate-400 hover:bg-slate-800/40 hover:text-white"
            }`}
            data-testid={`nav-${path.replace("/nosak/","")}`}>
            <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: isActive ? accent + "30" : "transparent" }}>
              <Icon size={13} style={{ color: isActive ? accent : undefined }} />
            </div>
            <span className="flex-1 truncate">{label}</span>
            {badge && (
              <span className="flex-shrink-0 w-4 h-4 rounded-full bg-red-500 text-[8px] font-black text-white flex items-center justify-center">
                {badge}
              </span>
            )}
            {isActive && <div className="w-1 h-4 rounded-full ml-auto flex-shrink-0" style={{ background: accent }} />}
          </NavLink>
        );
      })}
    </div>
  );
}

// ── LCFPI Sidebar ────────────────────────────────────────────────────────────
function LCFPISidebar({ onClose, location }) {
  const ACCENT = "#3b82f6";
  const items = [
    { label:"Dashboard",           path:"/lcfpi/dashboard",          icon:BarChart2   },
    { label:"Pipeline Digest",     path:"/lcfpi/digest",             icon:Activity    },
    { label:"Pipeline Register",   path:"/lcfpi/pipeline",           icon:Database    },
    { label:"Portfolio Analytics", path:"/lcfpi/portfolio",          icon:Activity    },
    { label:"Submit Project",      path:"/lcfpi/submit",             icon:PlusCircle  },
    { label:"Climate Assessment",  path:"/lcfpi/assessment",         icon:Leaf        },
    { label:"Finance Readiness",   path:"/lcfpi/readiness",          icon:TrendingUp  },
    { label:"Workflow Manager",    path:"/lcfpi/workflow",           icon:GitBranch   },
    { label:"Investment Profile",  path:"/lcfpi/investment-profile", icon:DollarSign  },
    { label:"DFI Matching Engine", path:"/lcfpi/dfi-matching",       icon:Globe       },
    { label:"Monetisation",        path:"/lcfpi/monetisation",       icon:BarChart2   },
    { label:"PDD Builder",         path:"/lcfpi/pdd-builder",        icon:FileText    },
    { label:"OCCE Gov Workspace",  path:"/lcfpi/gov-workspace",      icon:Activity    },
    { label:"Investor Workspace",  path:"/lcfpi/investor-workspace", icon:TrendingUp  },
    { label:"Data Rooms",          path:"/lcfpi/data-room",          icon:FolderOpen  },
    { label:"EOI Generator",       path:"/lcfpi/eoi-generator",      icon:FileText    },
    { label:"EOI Tracker",         path:"/lcfpi/eoi-tracker",        icon:Activity    },
    { label:"Eco-Labels",          path:"/lcfpi/eco-labels",         icon:Award       },
    { label:"Reports",             path:"/lcfpi/reports",            icon:Shield      },
    { label:"Settings",            path:"/lcfpi/settings",           icon:Settings    },
  ];
  return (
    <div className="space-y-0.5">
      <div className="px-3 py-2">
        <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-0.5">LCFPI</p>
        <p className="text-[8px] text-slate-600">Lagos Climate Finance Pipeline</p>
      </div>
      {items.map(({ label, path, icon: Icon }) => {
        const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
        return (
          <NavLink key={path} to={path} onClick={onClose}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
              isActive ? "bg-blue-900/30 text-blue-300" : "text-slate-400 hover:bg-slate-800/40 hover:text-white"
            }`}
            data-testid={`nav-lcfpi-${path.replace("/lcfpi/","")}`}>
            <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: isActive ? ACCENT + "30" : "transparent" }}>
              <Icon size={13} style={{ color: isActive ? ACCENT : undefined }} />
            </div>
            <span className="flex-1 truncate">{label}</span>
            {isActive && <div className="w-1 h-4 rounded-full ml-auto flex-shrink-0" style={{ background: ACCENT }} />}
          </NavLink>
        );
      })}
    </div>
  );
}

// ── MTN Nigeria Sidebar ───────────────────────────────────────────────────────
function MTNSidebar({ onClose, location }) {
  const items = [
    { label:"Command Center",        path:"/mtn/dashboard",      icon:BarChart2   },
    { label:"Data Workspace",        path:"/mtn/workspace",      icon:Database    },
    { label:"Climate Intelligence",  path:"/mtn/intelligence",   icon:Leaf        },
    { label:"SDG Impact",            path:"/mtn/sdg-impact",     icon:Activity    },
    { label:"Finance & Green Infra", path:"/mtn/finance",        icon:DollarSign  },
    { label:"Carbon Markets",        path:"/mtn/carbon-markets", icon:GitBranch   },
    { label:"PDD Readiness",         path:"/mtn/pdd-readiness",  icon:FileText    },
    { label:"ESG & IFRS S2",         path:"/mtn/esg-disclosure", icon:Shield      },
    { label:"Evidence Vault",        path:"/mtn/evidence",       icon:FolderOpen  },
    { label:"Settings",              path:"/mtn/settings",       icon:Settings    },
  ];
  const ACCENT = "#FFCB00";
  return (
    <div className="space-y-0.5">
      <div className="px-3 py-2">
        <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-0.5">MTN Nigeria</p>
        <p className="text-[8px] text-slate-600">Climate Intelligence Platform</p>
      </div>
      {items.map(({ label, path, icon: Icon }) => {
        const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
        return (
          <NavLink key={path} to={path} onClick={onClose}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-xs font-medium transition-all ${
              isActive ? "bg-slate-700/60 text-white" : "text-slate-400 hover:bg-slate-800/40 hover:text-white"
            }`}
            data-testid={`nav-mtn-${path.replace("/mtn/","")}`}>
            <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ background: isActive ? ACCENT + "30" : "transparent" }}>
              <Icon size={13} style={{ color: isActive ? ACCENT : undefined }} />
            </div>
            <span className="flex-1 truncate">{label}</span>
            {isActive && <div className="w-1 h-4 rounded-full ml-auto flex-shrink-0" style={{ background: ACCENT }} />}
          </NavLink>
        );
      })}
    </div>
  );
}

// ── Lagos State Sidebar ───────────────────────────────────────────────────────
function LAGOSSidebar({ onClose, location }) {
  const items = [
    { label: "Dashboard",             path: "/state/lagos/dashboard",         icon: BarChart2     },
    { label: "Entity Reporting",       path: "/state/lagos/reporting",          icon: ClipboardList },
    { label: "Climate & Circularity",  path: "/state/lagos/climate",            icon: Leaf          },
    { label: "SDG Contribution",       path: "/state/lagos/sdg",                icon: Activity      },
    { label: "Carbon Value Bridge",    path: "/state/lagos/carbon-value",       icon: GitBranch     },
    { label: "Carbon Strategy",        path: "/state/lagos/carbon-strategy",    icon: Layers        },
    { label: "Carbon Structuring",     path: "/state/lagos/carbon-structuring", icon: FolderOpen    },
    { label: "Financials",                        path: "/state/lagos/financials",                        icon: DollarSign    },
    { label: "Climate Investment Structuring",  path: "/state/lagos/climate-investment-structuring",  icon: Layers        },
    { label: "Evidence Vault",                  path: "/state/lagos/evidence",                          icon: FileText      },
    { label: "Reports",                path: "/state/lagos/reports",            icon: BarChart2     },
    { label: "State Intelligence",     path: "/state/lagos/intelligence",       icon: Activity      },
    { label: "Eco Labels",             path: "/state/lagos/eco-labels",         icon: Layers        },
    { label: "Settings",               path: "/state/lagos/settings",           icon: DollarSign    },
  ];
  return (
    <div className="space-y-0.5">
      <p className="text-[9px] text-slate-500 uppercase tracking-widest px-3 pb-1 pt-2 font-semibold">Lagos State Government</p>
      {items.map(({ label, path, icon: Icon }) => {
        const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
        return (
          <NavLink key={path} to={path} onClick={onClose}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              isActive ? "bg-emerald-900/20 text-emerald-300 border-l-2 border-emerald-400" : "text-slate-400 hover:bg-slate-800 hover:text-white"
            }`}>
            <Icon size={13} className={isActive ? "text-emerald-400" : "text-slate-500"} />
            <span className="flex-1 truncate">{label}</span>
          </NavLink>
        );
      })}
    </div>
  );
}

// ── NIRSAL Sidebar ─────────────────────────────────────────────────────────────
function NIRSALSidebar({ onClose, location }) {
  const items = [
    { label: "Dashboard",              path: "/corporate/nirsal/dashboard",         icon: BarChart2    },
    { label: "Portfolio Reporting",    path: "/corporate/nirsal/reporting",          icon: ClipboardList},
    { label: "Climate & Portfolio",    path: "/corporate/nirsal/climate",            icon: Leaf         },
    { label: "SDG Contribution",       path: "/corporate/nirsal/sdg",                icon: Activity     },
    { label: "Carbon Value Bridge",    path: "/corporate/nirsal/value-bridge",       icon: GitBranch    },
    { label: "Carbon Strategy",        path: "/corporate/nirsal/carbon-strategy",    icon: Layers       },
    { label: "Carbon Structuring",     path: "/corporate/nirsal/carbon-structuring", icon: FolderOpen   },
    { label: "Evidence Vault",         path: "/corporate/nirsal/evidence-vault",     icon: FileText     },
    { label: "Financials",             path: "/corporate/nirsal/financials",         icon: DollarSign   },
    { label: "Reports",                path: "/corporate/nirsal/reports",            icon: BarChart2    },
  ];
  return (
    <div className="space-y-0.5">
      <p className="text-[9px] text-slate-500 uppercase tracking-widest px-3 pb-1 pt-2 font-semibold">
        NIRSAL Green Credit Program
      </p>
      {items.map(({ label, path, icon: Icon }) => {
        const isActive = location.pathname === path || location.pathname.startsWith(path + "/");
        return (
          <NavLink key={path} to={path} onClick={onClose}
            className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
              isActive ? "bg-blue-900/30 text-blue-300 border-l-2 border-blue-400" : "text-slate-400 hover:bg-slate-800 hover:text-white"
            }`}>
            <Icon size={13} className={isActive ? "text-blue-400" : "text-slate-500"} />
            <span className="flex-1 truncate">{label}</span>
          </NavLink>
        );
      })}
    </div>
  );
}

// ── FI 9-Module sidebar nav (merged into Layout sidebar) ──────────────────
function FINav({ onClose }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const params    = new URLSearchParams(location.search);
  const fiModule  = params.get('m') || 'dashboard';
  const fiSection = params.get('s') || 'dashboard';

  return (
    <div className="space-y-0.5" data-testid="fi-module-nav">
      {FI_MODULES.map(mod => {
        const Icon     = mod.icon;
        const isActive = fiModule === mod.key;
        const isSingle = mod.sections.length === 1;
        return (
          <div key={mod.key}>
            <button
              onClick={() => {
                navigate(`/fi/workspace?m=${mod.key}&s=${mod.sections[0].key}`);
                onClose();
              }}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-[12px] font-medium border-l-2 transition-all ${
                isActive
                  ? "border-teal-500 text-teal-300 bg-teal-600/10"
                  : "border-transparent text-slate-500 hover:bg-slate-800/50 hover:text-slate-300"
              }`}
              data-testid={`fi-module-${mod.key}`}>
              <Icon size={13} className={`flex-shrink-0 ${isActive ? "text-teal-400" : ""}`} />
              <span className="flex-1 truncate">{mod.label}</span>
              {!isSingle && (
                <ChevronRight size={9} className={`flex-shrink-0 transition-transform ${isActive ? "rotate-90 text-teal-500" : "text-slate-700"}`} />
              )}
            </button>
            {isActive && !isSingle && (
              <div className="ml-4 pl-2 border-l border-white/8 mt-0.5 mb-1">
                {mod.sections.map(sec => (
                  <button key={sec.key}
                    onClick={() => {
                      navigate(`/fi/workspace?m=${mod.key}&s=${sec.key}`);
                      onClose();
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-left text-[12px] transition-all ${
                      fiSection === sec.key
                        ? "text-teal-300 bg-teal-600/10"
                        : "text-slate-500 hover:bg-white/5 hover:text-slate-300"
                    }`}
                    data-testid={`fi-nav-${sec.key}`}>
                    <div className={`w-1 h-1 rounded-full flex-shrink-0 ${fiSection === sec.key ? "bg-teal-400" : "bg-slate-700"}`} />
                    <span>{sec.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Role Navigation Configuration ─────────────────────────────────────────
// Master workspace nav sequences — locked per product specification
const ROLES_CONFIG = {
  sme: {
    homeRoute: "/sme/overview",
    icon: Building2,
    sections: [
      {
        title: "SME WORKSPACE",
        items: [
          { label: "SME Overview",                  path: "/sme/overview",              icon: LayoutDashboard },
          { label: "Monthly Input Data",            path: "/sme/monthly/inputs",        icon: Database        },
          { label: "Climate Snapshot",              path: "/sme/climate-snapshot",      icon: BarChart2       },
          { label: "Climate & Circularity Credentials", path: "/sme/credentials",       icon: Shield          },
          { label: "SDG Contribution",              path: "/sme/sdg",                   icon: Globe           },
          { label: "Portfolio Intelligence",        path: "/sme/monthly/trends",        icon: TrendingDown    },
          { label: "Evidence Vault",                path: "/evidence",                  icon: FolderOpen      },
          { label: "Reports",                       path: "/sme-reports",               icon: FileText        },
          { label: "Financing Readiness",           path: "/sme/financing-readiness",   icon: DollarSign      },
          { label: "Climate Training",              path: "/academy",                   icon: GraduationCap   },
        ],
      },
    ],
  },

  project: {
    homeRoute: null,   // dynamic — computed from user.default_workspace_path
    icon: Layers,
    sections: [],  // HousingProjectSidebar or GenericProjectSidebar handles this
  },

  mtn: {
    homeRoute: "/mtn/dashboard",
    icon: BarChart2,
    sections: [],  // MTNSidebar handles navigation
  },

  corporate: {
    homeRoute: "/corporate",
    icon: BarChart3,
    sections: [
      {
        title: "CORPORATE WORKSPACE",
        items: [
          { label: "Corporate Overview",            path: "/corporate",                         icon: LayoutDashboard },
          { label: "Monthly Input Data",            path: "/corporate/monthly/inputs",          icon: Database        },
          { label: "Climate Snapshot",              path: "/corporate/monthly/trends",          icon: BarChart2       },
          { label: "Climate & Circularity Credentials", path: "/corporate/credentials",        icon: Shield          },
          { label: "SDG Contribution",              path: "/corporate/sdg",                     icon: Globe           },
          { label: "Portfolio Intelligence",        path: "/corporate/portfolio",               icon: TrendingDown    },
          { label: "Carbon Monetisation",           path: "/corporate/monetisation",            icon: DollarSign      },
          { label: "Carbon Project Structuring",    path: "/corporate/carbon-structuring",      icon: Layers          },
          { label: "Evidence Vault",                path: "/evidence",                          icon: FolderOpen      },
          { label: "Reports",                       path: "/corporate/reports",                 icon: FileText        },
          { label: "Financing & Transition Readiness", path: "/corporate/financing-readiness",  icon: TrendingUp      },
          { label: "Climate Training",              path: "/academy",                           icon: GraduationCap   },
        ],
      },
    ],
  },

  fi: {
    homeRoute: "/fi/workspace",
    icon: TrendingUp,
    sections: [],   // FI workspace uses its own internal 9-module navigation — no outer nav items
  },

  states: {
    homeRoute: "/states/overview",
    icon: Map,
    sections: [
      {
        title: "STATES WORKSPACE",
        items: [
          { label: "State Overview",               path: "/states/overview",             icon: LayoutDashboard },
          { label: "State Climate Snapshot",       path: "/states/climate-snapshot",     icon: BarChart2       },
          { label: "Aggregated Projects",          path: "/states/aggregated-projects",  icon: FolderOpen      },
          { label: "Climate & Circularity Credentials", path: "/states/credentials",    icon: Shield          },
          { label: "SDG Contribution",             path: "/states/sdg",                  icon: Globe           },
          { label: "Carbon Pathways & Article 6",  path: "/states/carbon-pathways",      icon: GitBranch       },
          { label: "Programme Structuring (PoA)",  path: "/states/programmes",           icon: Layers          },
          { label: "Labeling & Classification",    path: "/states/labeling",             icon: Flag            },
          { label: "Reports & Regulatory Exports", path: "/states/reports",              icon: FileText        },
          { label: "Financing Pipeline",           path: "/states/financing",            icon: DollarSign      },
          { label: "Climate Training",             path: "/academy",                        icon: GraduationCap   },
        ],
      },
    ],
  },

  training: {
    homeRoute: "/academy",
    icon: GraduationCap,
    sections: [
      {
        title: "LEARNING FLOW",
        items: [
          { label: "Learning Dashboard", path: "/academy",             icon: LayoutDashboard, exact: true },
          { label: "Courses",            path: "/academy",             icon: BookOpen                     },
          { label: "My Progress",        path: "/academy",             icon: Activity                     },
          { label: "Applied Exercises",  path: "/assessments",         icon: ClipboardList                },
        ],
      },
      {
        title: "CREDENTIALS",
        items: [
          { label: "Certificates",       path: "/app-registry",        icon: Award                        },
          { label: "My Record",          path: "/reports",             icon: FileText                     },
        ],
      },
      {
        title: "SUPPORT",
        items: [
          { label: "Resource Library",   path: "/methodologies",       icon: BookMarked                   },
          { label: "Settings",           path: "/settings",            icon: Settings                     },
        ],
      },
    ],
  },
};

// ── Role Switcher Dropdown — hidden when user has only one workspace ──────────
function RoleSwitcher({ activeRole, onRoleSwitch, allowedRoles }) {
  const meta = ROLE_META[activeRole] || ROLE_META.sme;
  const RoleIcon = ROLES_CONFIG[activeRole]?.icon || Building2;
  const visibleRoles = ALL_ROLES.filter(r => allowedRoles.includes(r));

  // Single-workspace users: show a static badge instead of a dropdown
  if (visibleRoles.length <= 1) {
    return (
      <div
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold"
        style={{ backgroundColor: `${meta.color}18`, borderColor: `${meta.color}40`, color: meta.color }}
        data-testid="workspace-static-badge"
      >
        <RoleIcon size={13} />
        <span className="hidden sm:block">{meta.label}</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="role-switcher-trigger"
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all hover:opacity-90"
          style={{ backgroundColor: `${meta.color}18`, borderColor: `${meta.color}40`, color: meta.color }}
        >
          <RoleIcon size={13} />
          <span className="hidden sm:block">{meta.label}</span>
          <ChevronDown size={11} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56" data-testid="role-switcher-menu">
        <div className="px-3 py-2.5 border-b border-slate-100">
          <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Switch Workspace</p>
        </div>
        {visibleRoles.map(roleId => {
          const rm    = ROLE_META[roleId];
          const Ic    = ROLES_CONFIG[roleId]?.icon || Building2;
          const isCur = roleId === activeRole;
          return (
            <DropdownMenuItem
              key={roleId}
              onClick={() => !isCur && onRoleSwitch(roleId)}
              data-testid={`role-option-${roleId}`}
              className={`flex items-center gap-2 py-2.5 cursor-pointer ${isCur ? "bg-slate-50" : ""}`}
            >
              <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${rm.color}20` }}>
                <Ic size={13} style={{ color: rm.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{rm.label}</p>
                <p className="text-[10px] text-slate-400 truncate">{rm.fullLabel}</p>
              </div>
              {isCur && <Check size={13} className="text-[#059669] flex-shrink-0" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Role Switching Overlay ─────────────────────────────────────────────────
function SwitchingOverlay({ targetRole }) {
  const meta = ROLE_META[targetRole] || ROLE_META.sme;
  const Icon = ROLES_CONFIG[targetRole]?.icon || Building2;
  return (
    <div className="fixed inset-0 bg-[#0B1220]/85 backdrop-blur-sm z-[200] flex items-center justify-center"
      data-testid="switching-overlay">
      <div className="text-center">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse"
          style={{ backgroundColor: `${meta.color}25` }}>
          <Icon size={22} style={{ color: meta.color }} />
        </div>
        <p className="text-white font-semibold text-sm">Switching workspace...</p>
        <p className="text-slate-400 text-xs mt-1">{meta.fullLabel}</p>
      </div>
    </div>
  );
}

// ── Sidebar Nav Section ────────────────────────────────────────────────────
function NavSection({ title, items, roleColor, onClose }) {
  return (
    <div className="mb-3">
      {title && <p className="text-[8.5px] text-slate-700 uppercase tracking-[0.12em] font-bold px-3 mb-1">{title}</p>}
      {!title && <div className="h-1" />}
      {items.map(({ label, path, icon: Icon, exact, badge }) => (
        <NavLink
          key={`${title}-${label}`}
          to={path}
          end={!!exact}
          onClick={onClose}
          className={({ isActive }) =>
            `group flex items-center gap-2.5 px-3 py-2 rounded-r-lg text-[12.5px] font-medium transition-all duration-150 border-l-2 ${
              isActive
                ? "border-[#059669] bg-emerald-600/12 text-emerald-300"
                : "border-transparent text-slate-400 hover:bg-slate-800/50 hover:text-white hover:border-slate-600"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <Icon size={14} strokeWidth={isActive ? 2 : 1.75} className="flex-shrink-0" />
              <span className="flex-1 truncate">{label}</span>
              {badge && (
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded font-semibold bg-emerald-600/20 text-emerald-400">
                  {badge}
                </span>
              )}
            </>
          )}
        </NavLink>
      ))}
    </div>
  );
}

// ── SME Sidebar — uses ROLES_CONFIG nav sequence ─────────────────────────
function SmeSidebarSections({ roleColor, onClose, location }) {
  const teal = "#059669";
  const items = ROLES_CONFIG.sme.sections[0]?.items || [];
  return (
    <NavSection title="SME WORKSPACE" items={items} roleColor={teal} onClose={onClose} />
  );
}


// ── Housing Project Sidebar — lean, exec-facing, matches page content ─────
function HousingProjectSidebar({ onClose, location }) {
  const teal = "#059669";

  // Fix 5: Final sidebar structure — 10 items, no Project Admin, Evidence Vault in main nav
  const mainItems = [
    { label: "Housing Project",       path: "/housing-project",          icon: Building2, exact: true },
    { label: "Input Data",            path: "/housing/input-data",       icon: Database               },
    { label: "Climate Snapshot",      path: "/housing/climate-snapshot", icon: BarChart2              },
    { label: "SDG Contribution",      path: "/housing/sdg",              icon: Leaf                   },
    { label: "Carbon Strategy",       path: "/housing/carbon-strategy",  icon: GitBranch              },
    { label: "Carbon Structuring",    path: "/housing/carbon-structuring", icon: Layers               },
    { label: "Evidence Vault",        path: "/housing/evidence-vault",   icon: FolderOpen             },
    { label: "Reports",               path: "/housing/reports",          icon: FileText               },
    { label: "Financing",             path: "/housing/financing",        icon: DollarSign             },
    { label: "Climate Training",      path: "/housing/training",         icon: GraduationCap          },
  ];

  return (
    <div className="space-y-0.5">
      {/* Primary nav — 10 items, no admin section */}
      {mainItems.map(({ label, path, icon: Icon, exact }) => (
        <NavLink key={path} to={path} end={!!exact} onClick={onClose}
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-r-lg text-[12.5px] font-medium transition-all border-l-2 ${
              isActive
                ? "border-[#059669] bg-emerald-600/12 text-emerald-300"
                : "border-transparent text-slate-400 hover:bg-slate-800/50 hover:text-white hover:border-slate-600"
            }`
          }>
          {({ isActive }) => (
            <>
              <Icon size={14} strokeWidth={isActive ? 2 : 1.75} className="flex-shrink-0" />
              <span className="flex-1 truncate">{label}</span>
            </>
          )}
        </NavLink>
      ))}

    </div>
  );
}

// ── Generic Project Workspace Sidebar (multi-sector) ─────────────────────
function GenericProjectSidebar({ onClose, location, assessmentId }) {
  const id = assessmentId || location.pathname.match(/\/project\/(\d+)/)?.[1];
  const [projectName, setProjectName] = React.useState(() => {
    // Try cache immediately (no flash)
    try {
      const { getProjectNameFromCache } = require("@/hooks/useProjectWorkspace");
      return getProjectNameFromCache(id) || null;
    } catch { return null; }
  });

  React.useEffect(() => {
    if (!id) return;
    // Fetch project name if not cached yet
    import("@/api/client").then(({ default: client }) => {
      client.get(`/workspace/project/${id}/journey`)
        .then(r => { if (r.data?.name) setProjectName(r.data.name); })
        .catch(() => {});
    });
  }, [id]);

  if (!id) return null;

  const projectLabel = projectName
    ? (projectName.length > 22 ? projectName.slice(0, 21) + "…" : projectName)
    : "Project";

  const items = [
    { label: projectLabel,          path: `/project/${id}/workspace`,          icon: BarChart2,     title: projectName || "Project Overview" },
    { label: "Input Data",               path: `/project/${id}/input-data`,          icon: Database    },
    { label: "Climate & Circularity",    path: `/project/${id}/climate-snapshot`,    icon: BarChart2   },
    { label: "SDG Contribution",    path: `/project/${id}/sdg`,                 icon: Leaf        },
    { label: "Carbon Strategy",     path: `/project/${id}/carbon-strategy`,     icon: GitBranch   },
    { label: "Carbon Structuring",  path: `/project/${id}/carbon-structuring`,  icon: Layers      },
    { label: "Evidence Vault",      path: `/project/${id}/evidence-vault`,      icon: FolderOpen  },
    { label: "Reports",             path: `/project/${id}/reports`,             icon: FileText    },
    { label: "Financing",           path: `/project/${id}/financing`,           icon: DollarSign  },
    { label: "Climate Training",    path: `/project/${id}/training`,            icon: GraduationCap },
  ];
  return (
    <div className="space-y-0.5">
      {items.map(({ label, path, icon: Icon, title }) => (
        <NavLink key={path} to={path} onClick={onClose} title={title}
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-3 py-2 rounded-r-lg text-[12.5px] font-medium transition-all border-l-2 ${
              isActive
                ? "border-[#059669] bg-emerald-600/12 text-emerald-300"
                : "border-transparent text-slate-400 hover:bg-slate-800/50 hover:text-white hover:border-slate-600"
            }`
          }>
          {({ isActive }) => <><Icon size={14} strokeWidth={isActive?2:1.75} className="flex-shrink-0" /><span className="flex-1 truncate">{label}</span></>}
        </NavLink>
      ))}
    </div>
  );
}


// ── Main Layout ────────────────────────────────────────────────────────────
export default function Layout() {
  const { user, currentOrg, logout, activeRole, setActiveRole } = useAuth();
  const navigate    = useNavigate();
  const location    = useLocation();
  const [sidebarOpen,    setSidebarOpen]    = useState(false);
  const [isSwitching,    setIsSwitching]    = useState(false);
  const [switchTarget,   setSwitchTarget]   = useState(null);

  const initials  = user ? `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`.toUpperCase() : "U";
  const fullName  = user ? `${user.first_name} ${user.last_name}` : "User";

  // ── Admin context safety net ──────────────────────────────────────────────
  // If an admin user somehow ends up inside the Layout (stale URL, old bookmark),
  // redirect them immediately. All hook calls must precede this check.
  React.useEffect(() => {
    if (user?.role === "admin" && !location.pathname.startsWith("/admin")) {
      navigate("/admin", { replace: true });
    }
  }, [user?.role, location.pathname, navigate]);

  const roleConf  = ROLES_CONFIG[activeRole] || ROLES_CONFIG.sme;
  const roleMeta  = ROLE_META[activeRole]    || ROLE_META.sme;
  const RoleIcon  = roleConf.icon;

  // ── Route-based role detection — auto-switch sidebar for states/corporate/fi ──
  React.useEffect(() => {
    const path = location.pathname;
    if ((path.startsWith("/states/") || path === "/states") && activeRole !== "states") {
      setActiveRole("states");
    } else if (path.startsWith("/state/lagos") && activeRole !== "lagos_state") {
      setActiveRole("lagos_state");
    } else if (path.startsWith("/corporate/nirsal") && activeRole !== "nirsal") {
      setActiveRole("nirsal");
    } else if (path.startsWith("/nosak") && activeRole !== "nosak") {
      setActiveRole("nosak");
    } else if (path.startsWith("/mtn") && activeRole !== "mtn") {
      setActiveRole("mtn");
    } else if (path.startsWith("/lcfpi") && !["lcfpi","lcfpi_gov","lcfpi_pm","lcfpi_investor"].includes(activeRole)) {
      setActiveRole("lcfpi");
    } else if (path.startsWith("/institutions/dbn") && activeRole !== "dbn") {
      setActiveRole("dbn");
    } else if (path.startsWith("/corporate/nbplc") && activeRole !== "nbplc") {
      setActiveRole("nbplc");
    } else if (path.startsWith("/corporate/") && !path.startsWith("/corporate/nirsal") && !path.startsWith("/corporate/nbplc") && activeRole !== "corporate") {
      setActiveRole("corporate");
    } else if (path.startsWith("/fi/") && activeRole !== "fi") {
      setActiveRole("fi");
    } else if ((path.startsWith("/sme/") || path === "/sme") && activeRole !== "sme" && user?.user_roles?.includes("sme")) {
      setActiveRole("sme");
    }
  }, [location.pathname]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleRoleSwitch = (newRole) => {
    if (newRole === activeRole) return;

    // Resolve destination before showing overlay (prevents stale sidebar flash)
    let destination;
    if (newRole === "project") {
      // workspace_path is stored as JSON: { route: "/project/:id/workspace", user_id: N }
      // MUST parse — navigating raw JSON string causes invalid-URL failure → Housing flash
      const rawPath = localStorage.getItem("decarboniq_workspace_path");
      let resolvedRoute = null;
      if (rawPath) {
        try {
          const parsed = JSON.parse(rawPath);
          resolvedRoute = parsed.route || null;
        } catch {
          resolvedRoute = rawPath; // legacy: plain string stored directly
        }
      }
      destination = resolvedRoute || "/no-project-assigned";
    } else {
      destination = ROLES_CONFIG[newRole]?.homeRoute || "/dashboard";
    }

    // Show switch overlay, apply role + navigate immediately — no 550ms artificial delay
    setSwitchTarget(newRole);
    setIsSwitching(true);
    setActiveRole(newRole);
    navigate(destination);
    setSidebarOpen(false);

    // Clear overlay after a brief visual settle (200ms max — just enough for React to commit)
    setTimeout(() => {
      setIsSwitching(false);
      setSwitchTarget(null);
    }, 200);
  };

  const getBreadcrumb = () => {
    const parts = location.pathname.split("/").filter(Boolean);
    return parts.map(p => {
      // Replace numeric IDs with cached project name if available
      if (/^\d+$/.test(p)) {
        try {
          const { getProjectNameFromCache } = require("@/hooks/useProjectWorkspace");
          const name = getProjectNameFromCache(p);
          if (name) return name.length > 20 ? name.slice(0, 19) + "…" : name;
        } catch {}
        return "Project";   // clean fallback — not "#2"
      }
      return p.charAt(0).toUpperCase() + p.slice(1).replace(/-/g, " ");
    });
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="h-14 flex items-center px-4 border-b border-slate-800 flex-shrink-0">
        <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
      </div>

      {/* Org + Role Context */}
      <div className="px-3 pt-4 pb-2 flex-shrink-0">
        {currentOrg && (
          <div className="p-3 bg-slate-800/50 rounded-xl border border-slate-700/60 mb-2">
            <p className="text-[9px] text-slate-500 uppercase tracking-[0.12em] font-semibold mb-0.5">Organization</p>
            <p className="text-sm text-white font-medium truncate leading-snug">{currentOrg.name}</p>
          </div>
        )}
        {/* Role badge */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border"
          style={{ backgroundColor: `${roleMeta.color}12`, borderColor: `${roleMeta.color}30` }}
          data-testid="role-badge">
          <RoleIcon size={13} style={{ color: roleMeta.color }} />
          <div className="min-w-0">
            <p className="text-[9px] text-slate-500 uppercase tracking-[0.12em] font-semibold">Workspace</p>
            <p className="text-xs font-semibold truncate" style={{ color: roleMeta.color }}>{roleMeta.label}</p>
          </div>
        </div>
      </div>

      {/* Dynamic Nav — SME gets journey-aware sidebar, project (housing) gets housing sidebar,
           FI workspace uses merged 9-module nav (no separate internal sidebar),
           generic project routes get sector-aware generic sidebar */}
      <nav className="flex-1 px-3 pt-2 overflow-y-auto" data-testid="role-nav">
        {/* During workspace switch: show loading skeleton — never show stale sidebar content */}
        {isSwitching ? (
          <div className="space-y-1.5 px-1 pt-2">
            {[70, 55, 65, 45, 70, 55, 60].map((w, i) => (
              <div key={i} className={`h-7 rounded-lg bg-slate-800/40 animate-pulse`} style={{ width: `${w}%` }} />
            ))}
          </div>
        ) : activeRole === "sme"
          ? <SmeSidebarSections roleColor={roleMeta.color} onClose={() => setSidebarOpen(false)} location={location} />
          : location.pathname.startsWith("/state/lagos")
          ? <LAGOSSidebar onClose={() => setSidebarOpen(false)} location={location} />
          : location.pathname.startsWith("/corporate/nirsal")
          ? <NIRSALSidebar onClose={() => setSidebarOpen(false)} location={location} />
          : location.pathname.startsWith("/nosak")
          ? <NosAKSidebar onClose={() => setSidebarOpen(false)} location={location} />
          : location.pathname.startsWith("/mtn")
          ? <MTNSidebar onClose={() => setSidebarOpen(false)} location={location} />
          : location.pathname.startsWith("/lcfpi")
          ? <LCFPISidebar onClose={() => setSidebarOpen(false)} location={location} />
          : location.pathname.startsWith("/corporate/nbplc")
          ? <NBPLCSidebar onClose={() => setSidebarOpen(false)} location={location} />
          : activeRole === "corporate" && location.pathname.startsWith("/corporate")
          ? <DangoteSidebar onClose={() => setSidebarOpen(false)} location={location} />
          : activeRole === "project"
          ? (/\/housing-project|\/housing\/|^\/housing$/.test(location.pathname)
              ? <HousingProjectSidebar onClose={() => setSidebarOpen(false)} location={location} />
              : <GenericProjectSidebar onClose={() => setSidebarOpen(false)} location={location} />)
          : activeRole === "fi"
          ? <FINav onClose={() => setSidebarOpen(false)} />
          : roleConf.sections.map(section => (
            <NavSection
              key={section.title}
              title={section.title}
              items={section.items}
              roleColor={roleMeta.color}
              onClose={() => setSidebarOpen(false)}
            />
          ))
        }
      </nav>

      {/* Bottom: admin + user */}
      <div className="px-3 pb-4 border-t border-slate-800 pt-3 flex-shrink-0 space-y-0.5">
        {user?.role === "admin" && (
          <NavLink to="/admin" onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-r-lg text-[13px] font-medium border-l-2 transition-all ${
                isActive ? "border-amber-500 text-amber-400 bg-amber-600/10" : "border-transparent text-slate-500 hover:bg-slate-800/50 hover:text-white"
              }`
            }>
            {({ isActive }) => <><Shield size={14} strokeWidth={isActive?2:1.75} /> Admin Console</>}
          </NavLink>
        )}
        {user?.role === "admin" && (
          <NavLink to="/admin/reviews" onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-r-lg text-[13px] font-medium border-l-2 transition-all ${
                isActive ? "border-indigo-500 text-indigo-400 bg-indigo-600/10" : "border-transparent text-slate-500 hover:bg-slate-800/50 hover:text-white"
              }`
            }>
            {({ isActive }) => <><GraduationCap size={14} strokeWidth={isActive?2:1.75} /> Analyst Reviews</>}
          </NavLink>
        )}
        {user?.role === "admin" && (
          <NavLink to="/admin/verifiers" onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-r-lg text-[13px] font-medium border-l-2 transition-all ${
                isActive ? "border-violet-500 text-violet-400 bg-violet-600/10" : "border-transparent text-slate-500 hover:bg-slate-800/50 hover:text-white"
              }`
            }>
            {({ isActive }) => <><Shield size={14} strokeWidth={isActive?2:1.75} className="opacity-60" /> Verifier Registry</>}
          </NavLink>
        )}
        {user?.role === "admin" && (
          <NavLink to="/cai/reporting" onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-r-lg text-[13px] font-medium border-l-2 transition-all ${
                isActive ? "border-teal-500 text-teal-400 bg-teal-600/10" : "border-transparent text-slate-500 hover:bg-slate-800/50 hover:text-white"
              }`
            }>
            {({ isActive }) => <><Flag size={14} strokeWidth={isActive?2:1.75} /> Article 6 Reporting</>}
          </NavLink>
        )}
        {user?.role === "admin" && (
          <NavLink to="/admin/enterprise-crm" onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-r-lg text-[13px] font-medium border-l-2 transition-all ${
                isActive ? "border-emerald-500 text-emerald-400 bg-emerald-600/10" : "border-transparent text-slate-500 hover:bg-slate-800/50 hover:text-white"
              }`
            }>
            {({ isActive }) => <><Building2 size={14} strokeWidth={isActive?2:1.75} /> Enterprise CRM</>}
          </NavLink>
        )}
        {user?.role === "admin" && (
          <NavLink to="/admin/pending-registrations" onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-r-lg text-[13px] font-medium border-l-2 transition-all ${
                isActive ? "border-amber-500 text-amber-400 bg-amber-600/10" : "border-transparent text-slate-500 hover:bg-slate-800/50 hover:text-white"
              }`
            }>
            {({ isActive }) => <><Clock size={14} strokeWidth={isActive?2:1.75} /> Pending Registrations</>}
          </NavLink>
        )}
        {/* Upgrade CTA — hidden for project role, SME, NB workspace, MTN, Nosak, Lagos (enterprise workspaces) */}
        {user && activeRole !== "sme" && activeRole !== "project" && activeRole !== "nbplc" && activeRole !== "nirsal" && activeRole !== "lagos_state"
          && activeRole !== "mtn" && activeRole !== "nosak"
          && !["lcfpi","lcfpi_gov","lcfpi_pm","lcfpi_investor"].includes(activeRole)
          && !location.pathname.startsWith("/corporate/nbplc")
          && !["institutional","enterprise"].includes(user.subscription_tier) && (
          <NavLink to="/upgrade" onClick={() => setSidebarOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-r-lg text-[13px] font-medium border-l-2 transition-all mt-1 ${
                isActive ? "border-emerald-500 text-emerald-400 bg-emerald-600/10" : "border-transparent text-emerald-500 hover:bg-emerald-600/10 hover:text-emerald-400"
              }`
            }>
            {({ isActive }) => <><Zap size={14} strokeWidth={isActive?2:1.75} /> Upgrade Plan</>}
          </NavLink>
        )}
        {/* User row */}
        <div className="flex items-center gap-2.5 px-3 py-2 mt-1">
          <Avatar className="h-7 w-7 flex-shrink-0">
            <AvatarFallback className="bg-emerald-700 text-white text-xs font-bold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-white font-medium truncate leading-snug">{fullName}</p>
            <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
          </div>
          <button onClick={logout} title="Sign out"
            className="text-slate-500 hover:text-red-400 transition-colors flex-shrink-0">
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#F8FAFC] overflow-hidden">
      {/* Micro-onboarding welcome toast — fires once after signup */}
      <WelcomeToast />
      {/* Switching workspace overlay */}
      {isSwitching && <SwitchingOverlay targetRole={switchTarget} />}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-58 flex-shrink-0 bg-[#0B1220] border-r border-slate-800/60 flex-col h-full" style={{ width: 232 }} data-testid="sidebar">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-58 bg-[#0B1220] flex flex-col h-full" style={{ width: 232 }}>
            <SidebarContent />
          </div>
          <div className="flex-1 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top nav */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-4 sm:px-6 gap-3 flex-shrink-0" data-testid="top-nav">
          {/* Mobile hamburger */}
          <button className="lg:hidden text-slate-500 hover:text-slate-800 flex-shrink-0" onClick={() => setSidebarOpen(true)}>
            <Menu size={20} />
          </button>

          {/* Role switcher — Fix 4: Training is NOT a workspace; embedded in project sidebar */}
          <RoleSwitcher
            activeRole={activeRole}
            onRoleSwitch={handleRoleSwitch}
            allowedRoles={user?.role === "admin"
              ? ALL_ROLES.filter(r => r !== "training")
              : (user?.user_roles?.filter(r => r !== "training") || [activeRole])}
          />

          {/* Breadcrumb */}
          <div className="flex items-center gap-1 text-sm text-slate-400 flex-1 overflow-hidden min-w-0">
            {getBreadcrumb().map((crumb, i, arr) => (
              <React.Fragment key={i}>
                {i > 0 && <ChevronRight size={13} className="text-slate-300 flex-shrink-0" />}
                <span className={`truncate ${i === arr.length - 1 ? "text-slate-700 font-medium" : "hidden sm:block"}`}>
                  {crumb}
                </span>
              </React.Fragment>
            ))}
          </div>

          {/* Right: bell + user */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors">
              <Bell size={15} />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                  <Avatar className="h-6 w-6">
                    <AvatarFallback className="bg-[#059669] text-white text-xs font-bold">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-slate-700 hidden md:block">{user?.first_name}</span>
                  <ChevronDown size={12} className="text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-3 py-2.5">
                  <p className="text-sm font-semibold">{fullName}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                  <p className="text-[10px] text-slate-300 mt-0.5 uppercase tracking-wide"
                    style={{ color: roleMeta.color }}>{roleMeta.label} workspace</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <User size={13} className="mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings?tab=org")}>
                  <Building2 size={13} className="mr-2" /> Organization
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")}>
                  <Settings size={13} className="mr-2" /> Settings
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")}>
                    <Shield size={13} className="mr-2" /> Admin Console
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                  <LogOut size={13} className="mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto bg-[#F8FAFC]" data-testid="main-content">
          {/* During workspace switch: show clean loading state, not stale content */}
          {isSwitching ? (
            <div className="flex items-center justify-center h-full min-h-[60vh]">
              <div className="text-center">
                <div className="w-7 h-7 border-2 border-[#059669] border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-sm text-slate-400">Loading workspace...</p>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}
